import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import useVideoValidation from "../../../../../../hooks/useVideoValidation";
import useImageValidation from "../../../../../../hooks/useImageValidation";
import { courseService } from "../../../../../../services/course.service";
import userImg from "../../../../../../assets/images/user_filled.svg";
import videoImg from "../../../../../../assets/images/video.svg";
import { deleteButtonIcon } from "../../../../../../icon/delete-button-preview";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import { CourseData } from "../../../../../../types";
import { useNavigate } from "react-router-dom";

const courseSchema = Yup.object().shape({
  name: Yup.string()
    .max(120, "Заголовок должен быть не более 20 символов")
    .required("Укажите заголовок курса"),
  subtitle: Yup.string()
    .max(100, "Подзаголовок должен быть не более 100 символов")
    .required("Укажите подзаголовок курса"),
  description: Yup.string()
    .min(1, "Описание должно содержать минимум 200 символов")
    .max(1000, "Описание должно быть не более 1000 символов")
    .required("Укажите описание курса"),
  subject: Yup.string()
    .max(100, "Предмет должен быть не более 100 символов")
    .required("Укажите основной предмет курса"),
  language_id: Yup.string().required("Выберите язык курса"),
  level_id: Yup.string().required("Выберите уровень курса"),
  category_id: Yup.string().required("Выберите категорию курса"),
  //   image: Yup.string().required("Загрузите изображение курса"),
});

const ConstructorStepThree = ({ course, setStep }: any) => {
  const { validateVideo, ErrorModal: VideoErrorModal } = useVideoValidation();
  const { validateImage, ErrorModal: ImageErrorModal } = useImageValidation();
  const navigate = useNavigate();

  const {
    data: courseData,
    isLoading,
    isError,
  } = useQuery<CourseData, Error>({
    queryKey: ["courseData", course.id],
    queryFn: () => courseService.getCourseDetails(course.id),
  });

  const { data: languages } = useQuery({
    queryKey: ["Language"],
    queryFn: courseService.getCourseLanguages,
  });
  const { data: levels } = useQuery({
    queryKey: ["Level"],
    queryFn: courseService.getCourseLevels,
  });
  const { data: categories } = useQuery({
    queryKey: ["Categorie"],
    queryFn: courseService.getCourseCategory,
  });

  if (isLoading) {
    return <div>Загрузка...</div>;
  }

  if (isError) {
    return <div>Ошибка загрузки данных курса</div>;
  }
  return (
    <Formik
      initialValues={{
        name: courseData?.name || "",
        subtitle: courseData?.subtitle || "",
        description: courseData?.description || "",
        subject: courseData?.subject || "",
        image: courseData?.image || "",
        video: courseData?.video || "",
        category_id: courseData?.category_id || "",
        language_id: "7cdf01d8-8b66-4be6-a5a8-825f52415a36",
        level_id: "7cdf01d8-8b66-4be6-a5a8-825f52415a36",
      }}
      validationSchema={courseSchema}
      onSubmit={async (values, { setSubmitting }) => {
        const updatedData = {
          name: values.name,
          subtitle: values.subtitle,
          description: values.description,
          subject: values.subject,
          image: values.image || "",
          video: values.video || "",
          category_id: values.category_id,
          language_id: values.language_id,
          level_id: values.level_id,
        };
        try {
          await courseService.updateCourseDetail(course.id, updatedData);
          setStep(4);
        } catch (error) {
          console.error("Ошибка при обновлении курса", error);
        }

        setSubmitting(false);
      }}>
      {({ values, handleChange, setFieldValue, errors, touched }) => (
        <Form>
          <VideoErrorModal />
          <ImageErrorModal />
          <div className="course-content">
            <div className="constructor-content-title">Страница курса</div>
            <div className="corse-content-description">
              Страница вашего курса имеет решающее значение для вашего успеха в
              HiStudy. Грамотное оформление страницы курса также может помочь
              вам повысить видимость курса в результатах поиска, например в
              поисковой системе Google. Во время заполнения данного раздела
              подумайте о том, что может побудить учащихся зарегистрироваться на
              ваш курс.
            </div>

            <div className="constructor-content-title mt-52">
              Заголовок курса <span>обязательно</span>
            </div>
            <div className="corse-content-description">
              Название курса должно быть привлекательным, информативным и
              оптимизированным для поиска.
            </div>
            <Field
              name="name"
              as={TextField}
              className="without-label"
              variant="outlined"
              placeholder="Введите заголовок"
              sx={{
                marginTop: "12px",
                width: 600,
                "@media (max-width:1000px)": {
                  width: "100%",
                },
              }}
              error={Boolean(errors.name && touched.name)}
              helperText={errors.name && touched.name && errors.name}
            />
            <div
              className={
                values.name.length > 120
                  ? "description-input-counter error-text"
                  : "description-input-counter"
              }>
              {values.name.length}/120
            </div>

            <div className="constructor-content-title mt-52">
              Подзаголовок курса <span>обязательно</span>
            </div>
            <div className="corse-content-description">
              Используйте 1 или 2 ключевых слова, и упомяните 3–4 наиболее
              важные сферы, затрагиваемые в вашем курсе.
            </div>
            <Field
              name="subtitle"
              as={TextField}
              variant="outlined"
              placeholder="Введите подзаголовок"
              className="without-label"
              error={Boolean(errors.subtitle && touched.subtitle)}
              helperText={
                errors.subtitle && touched.subtitle && errors.subtitle
              }
            />
            <div
              className={
                values.subtitle.length > 100
                  ? "description-input-counter error-text"
                  : "description-input-counter"
              }>
              {values.subtitle.length}/100
            </div>

            <div className="constructor-content-title mt-52">
              Описание курса <span>обязательно</span>
            </div>
            <div className="corse-content-description">
              Описание должно содержать не менее 200 символов.
            </div>
            <Field
              name="description"
              as={TextField}
              multiline
              variant="outlined"
              placeholder="Введите описание курса"
              className="without-label"
              error={Boolean(errors.description && touched.subtitle)}
              helperText={
                errors.description && touched.description && errors.description
              }
            />
            <div
              className={
                values.description.length > 1000
                  ? "description-input-counter error-text"
                  : "description-input-counter"
              }>
              {values.description.length}/1000
            </div>

            <div className="constructor-content-title mt-52">
              Основная информация <span>обязательно</span>
            </div>
            <div className="corse-content-description">
              Укажите язык, уровень, категория и подкатегорию курса.
            </div>
            <div className="select-group">
              <div className="select-group-top">
                <FormControl
                  error={Boolean(touched.language_id && errors.language_id)}>
                  <InputLabel id="language-label">Выберите язык</InputLabel>
                  <Field
                    as={Select}
                    name="language_id"
                    labelId="language-label"
                    label="Выберите язык"
                    onChange={handleChange}
                    value={values.language_id ?? ""}>
                    {languages?.map(
                      (language: { id: string; name: string }) => (
                        <MenuItem key={language.id} value={language.id}>
                          {language.name}
                        </MenuItem>
                      )
                    )}
                  </Field>
                  {touched.language_id && errors.language_id && (
                    <FormHelperText>{errors.language_id}</FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  error={Boolean(touched.level_id && errors.level_id)}>
                  <InputLabel id="level-label">
                    Выберите уровень курса
                  </InputLabel>
                  <Field
                    as={Select}
                    name="level_id"
                    labelId="level-label"
                    label="Выберите уровень курса"
                    onChange={handleChange}
                    value={values.level_id ?? ""}>
                    {levels?.map((level: { id: string; name: string }) => (
                      <MenuItem key={level.id} value={level.id}>
                        {level.name}
                      </MenuItem>
                    ))}
                  </Field>
                  {touched.level_id && errors.level_id && (
                    <FormHelperText>{errors.level_id}</FormHelperText>
                  )}
                </FormControl>
              </div>

              <FormControl
                error={Boolean(touched.category_id && errors.category_id)}>
                <InputLabel id="category-label">Выберите категорию</InputLabel>
                <Field
                  as={Select}
                  name="category_id"
                  labelId="category-label"
                  label="Выберите категорию"
                  onChange={handleChange}
                  value={values.category_id ?? ""}>
                  {categories?.map((category: { id: string; name: string }) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Field>
                {touched.category_id && errors.category_id && (
                  <FormHelperText>{errors.category_id}</FormHelperText>
                )}
              </FormControl>
            </div>

            <div className="constructor-content-title mt-52">
              Каков основной предмет вашего курса? <span>обязательно</span>
            </div>
            <div className="corse-content-description">
              Каждая отдельная выбранная тема должна конкретно и точно описывать
              содержание вашего курса, не обобщая излишне. Например для курса
              "Полный курс по теннису" необходимо выбрать тему "Теннис", а не
              "Подача в теннисе" (конкретная, но не точная) или "Спорт" (точная,
              но не конкретная).
              <span className="more-info">Узнать больше.</span>
            </div>
            <Field
              name="subject"
              as={TextField}
              variant="outlined"
              placeholder="Введите предмет"
              className="without-label"
              error={Boolean(errors.subject && touched.subject)}
              helperText={errors.subject && touched.subject && errors.subject}
            />
            <div
              className={
                values.subject.length > 100
                  ? "description-input-counter error-text"
                  : "description-input-counter"
              }>
              {values.subject.length}/100
            </div>

            <div className="constructor-content-title mt-52">
              Изображение курса
            </div>
            <div className="corse-content-description">
              Загрузите изображение своего курса здесь. Чтобы загрузка была
              успешной, оно должно соответствовать
              <span className="more-info">
                {" "}
                стандартам качества изображений курсов.
              </span>{" "}
              Важные требования: 750×422 пикселей; jpg, jpeg, gif, png, без
              текста на изображении.
            </div>
            {values.image && values.image !== "" ? (
              <Box sx={{ position: "relative" }}>
                <div className="image-preview">
                  <img
                    className="img-preview"
                    src={values.image}
                    alt="Превью курса"
                  />
                  <button
                    type="button"
                    className="delete-button"
                    onClick={async () => {
                      if (values.image) {
                        try {
                          const url = values.image.split("/").pop();
                          await courseService.deletePreview(url);
                          setFieldValue("image", "");
                        } catch (error) {
                          console.error(
                            `Ошибка при удалении изображения: ${error}`
                          );
                          setFieldValue("image", "");
                        }
                      }
                    }}>
                    {deleteButtonIcon}
                  </button>
                </div>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  pt: "12px",
                }}>
                <div
                  className={`file-select ${
                    touched.image && errors.image ? "file-select-error" : ""
                  }`}>
                  <img src={userImg} alt="default" />
                  <label htmlFor="img">Загрузить изображение</label>
                  <input
                    type="file"
                    style={{ display: "none" }}
                    id="img"
                    onChange={async (event) => {
                      if (event.target.files && event.target.files.length > 0) {
                        const file = event.target.files[0];
                        const errorMessage = await validateImage(file);
                        if (errorMessage) {
                          return;
                        }
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onload = async () => {
                          const base64 = reader.result as string;
                          try {
                            const response = await courseService.loadImage(
                              course.id,
                              base64
                            );
                            const imageURL = response.data.image;
                            setFieldValue("image", imageURL);
                          } catch (error) {
                            console.error(
                              "Ошибка при загрузке изображения",
                              error
                            );
                          }
                        };
                      }
                    }}
                  />
                </div>
              </Box>
            )}
            <div className="constructor-content-title mt-52">
              Рекламное видео
            </div>
            <div className="corse-content-description">
              Размещение проморолика ― это быстрый и эффективный способ для
              ознакомления студентов с содержанием вашего курса. Студенты,
              которые рассматривают возможность прохождения вашего курса, с
              большей вероятностью зарегистрируются на него, если ваше рекламное
              видео сделано качественно.
              <span className="more-info">
                {" "}
                Узнайте, как сделать отличный проморолик.
              </span>
            </div>

            {values.video ? (
              <Box sx={{ position: "relative" }}>
                <div className="video-preview">
                  <video className="vid-preview" src={values.video} controls />
                  <button
                    className="delete-button"
                    onClick={async () => {
                      if (values.video) {
                        try {
                          const url = values.video.split("/").pop();
                          await courseService.deleteVideo(url);
                          setFieldValue("video", "");
                        } catch (error) {
                          console.error(`Ошибка при удалении видео: ${error}`);
                        }
                      }
                    }}>
                    {deleteButtonIcon}
                  </button>
                </div>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  pt: "12px",
                }}>
                <div
                  className={`file-select ${
                    touched.video && errors.video ? "file-select-error" : ""
                  }`}>
                  <img src={videoImg} alt="default" />
                  <label htmlFor="video">Загрузить видео</label>
                  <input
                    type="file"
                    style={{ display: "none" }}
                    id="video"
                    onChange={async (event) => {
                      if (event.target.files && event.target.files[0]) {
                        const file = event.target.files[0];
                        const errorMessage = await validateVideo(file);
                        if (!errorMessage) {
                          const formData = new FormData();
                          formData.append("file", file);
                          try {
                            const response = await courseService.loadVideo(
                              formData
                            );
                            setFieldValue("video", response.data.url);
                          } catch (error) {
                            console.error("Ошибка при загрузке видео", error);
                          }
                        }
                      }
                    }}
                  />
                </div>
              </Box>
            )}
          </div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              backgroundColor: "#F5F8FB",
              border: "1px solid #E5E5E5",
              padding: "12px 32px 12px 32px",
              marginTop: "52px",
              "@media (max-width:1000px)": {
                backgroundColor: "transparent",
                border: "none",
                width: "100%",
                padding: 0,
              },
            }}>
            <Box>
              <Button
                sx={{ marginLeft: "12px" }}
                variant="outlined"
                onClick={() => setStep(2)}>
                Назад
              </Button>
            </Box>

            <Box sx={{ display: "flex", gap: "12px", marginLeft: "auto" }}>
              <Button
                type="submit"
                variant="outlined"
                onClick={() => navigate("/panel")}>
                Сохранить как черновик
              </Button>

              <Button type="submit" variant="contained">
                Далее
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default ConstructorStepThree;
